import React, { useEffect } from "react"
import { Grid, Box, CircularProgress } from "@mui/material"
import { FlagsIconSeeker } from "@ui-seeker/core"
import { getExcel, translateCountryName } from "@ui-seeker/utils"
import PageWrapper from "Pages/@PageWrapper"
import { AppCardTitle, AppCrudTable, AppToolbarCreateButton, AppButtonIcon, AppOfficesBlock, AppProviderBlock, AppResultsSkeleton } from "@components"
import { BACKEND_APIS } from "@constants"
import { AUTO_EUROPE_TOOL_ROUTES } from "@constants/routes"
import {
	autoEuropeProviderNormalizationDrawerData,
	autoEuropeProviderNormalizationListData,
	autoEuropeProviderNormalizationIsLoading,
	autoEuropeProviderNormalizationIsSubmitting,
} from "Pages/AutoEuropeTool/ProviderNormalization/@signals"

import AutoEuropeProviderNormalizationDrawer from "Pages/AutoEuropeTool/ProviderNormalization/Drawer"
import useAutoEuropeProviderNormalizationTable from "Pages/AutoEuropeTool/ProviderNormalization/@hooks/useTable"
import useAutoEuropeProviderNormalizationFormSubmit from "Pages/AutoEuropeTool/ProviderNormalization/@hooks/useFormSubmit"
import useAutoEuropeProviderNormalizationActions from "Pages/AutoEuropeTool/ProviderNormalization/@hooks/useActions"
import useAutoEuropeProviderNormalizationEdit from "Pages/AutoEuropeTool/ProviderNormalization/@hooks/useEdit"

const EMPTY_FORM = { provider: null, autoEuropeProvider: '', filterMode: 'officeCountry', officeCountries: [], offices: [] }

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useAutoEuropeProviderNormalizationEdit(row.normalizationRecordId)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.normalizationRecordId) }}
			/>
		</Box>
	)
}

export default function AutoEuropeProviderNormalizationPage() {
	const { getTableData } = useAutoEuropeProviderNormalizationTable()
	const { onSubmit } = useAutoEuropeProviderNormalizationFormSubmit({ getTableData })
	const { onDelete } = useAutoEuropeProviderNormalizationActions()

	useEffect(() => {
		getTableData()
	}, [])

	return <PageWrapper
		head={`DS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.PROVIDER_NORMALIZATION_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.PROVIDER_NORMALIZATION_SUBTITLE}
	>
		<AutoEuropeProviderNormalizationDrawer onSubmit={onSubmit} isLoading={autoEuropeProviderNormalizationIsSubmitting.value}/>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeProviderNormalizationIsLoading.value
					? <AppResultsSkeleton rows={7} columns={6}/>
					: <AppCrudTable
						title={<AppCardTitle icon="arrows-rotate" title="Provider Normalization Configuration"/>}
						data={autoEuropeProviderNormalizationListData.value}
						columns={[
							{ name: 'providerName', label: 'Data Seekers Provider', options: { customBodyRowRender: v => centered(v ? <AppProviderBlock provider={v?.toUpperCase?.() ?? v}/> : v) } },
							{ name: 'autoEuropeProvider', label: 'Auto Europe Provider', options: { customBodyRowRender: v => centered(v) } },
							{ name: 'officeCountries', label: 'Office Countries', options: { customBodyRowRender: v => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
		{(v ?? []).length
			? (v ?? []).map(c => (
				<Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<FlagsIconSeeker countryName={c}/>
					<span>{translateCountryName(c) || c}</span>
				</Box>
			))
			: centered('Any')
		}
	</Box>
) } },
							{ name: 'officesName', label: 'Offices', options: { customBodyRowRender: v => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
		{(v ?? []).length
			? (v ?? []).map(o => <AppOfficesBlock key={o.officeID ?? o} name={o.name ?? o} type={o.type} country={o.country}/>)
			: centered('Any')
		}
	</Box>
) } },
							{ name: 'provider', label: 'Actions', options: { filter: false, customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete}/> } },
						]}
						options={{
							customToolbar: () => <AppToolbarCreateButton
								label="Create"
								icon="file-plus"
								onClick={() => autoEuropeProviderNormalizationDrawerData.value = { ...EMPTY_FORM }}
								/>,
							onDownload: () => getExcel(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/provider-normalization/list/excel`, {}),
						}}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}
